// [TODO] make this a function
import { Component } from "react"
import PropTypes from "prop-types"
import { find, isEmpty, map, get, toUpper } from "lodash"
import { Card, CardHeader, IconButton, Tooltip, List, ListItem, ListItemText } from "@mui/material"
import { Close, Cancel, InfoOutlined } from "@mui/icons-material"
import { PERIOD, DATE, SEARCHABLE_MENU_ITEM } from "helpers/shapes"
import PeriodSelector from "components/period-selector"
import SelectedDate from "components/selected-date"
import NoSecurity from "./components/no-security"
import WatchlistsMenu from "modules/watchlists-menu"
import SecurityProfilePerformance from "modules/security-profile-performance"
import SecurityProfileRisk from "modules/security-profile-risk"
import SecurityProfileExposure from "modules/security-profile-exposure"
import SecurityProfileContentSet from "modules/security-profile-content-set"
import SecurityProfileComposition from "modules/security-profile-composition"
import SecurityProfileForecast from "modules/security-profile-forecast"
import UnifiedSearch from "modules/unified-search"
import ModelSelector from "modules/model-selector"
import SearchableMenu from "components/searchable-menu"
import { getBackwardsCompatibleSelectedType } from "helpers/comparison"
import { SAME_DAY } from "helpers/dates"
import { getToday } from "helpers/types/dates"
import saveRecentResources from "hocs/saveRecentResources"
import CompositeSecurityLink from "components/composite-security-link"
import SecurityProfileFactorSensitivity from "modules/security-profile-factor-sensitivity"
import SecurityProfileToResearchTopic from "components/security-profile-to-researchTopic"
import SecurityPrice from "components/intraday/security-price"

import "./style.scss"

class SecurityProfile extends Component {
  static propTypes = {
    account: PropTypes.string.isRequired,
    period: PERIOD.isRequired,
    date: DATE,
    startDate: DATE,
    endDate: DATE,
    earliestDate: DATE,
    latestDate: DATE,
    setPeriod: PropTypes.func.isRequired,
    resetDate: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    modelId: PropTypes.string.isRequired,
    security: PropTypes.object,
    rawSecurityId: PropTypes.object,
    securityWatchlists: PropTypes.array,
    watchlistsMenuVisible: PropTypes.bool,
    toggleWatchlistsMenu: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    base: PropTypes.object,
    baseName: PropTypes.string,
    removeBase: PropTypes.func.isRequired,
    displayType: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    setDisplayType: PropTypes.func.isRequired,
    datesAdjusted: PropTypes.bool.isRequired,
    setModel: PropTypes.func,
    models: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    trackCSVDownload: PropTypes.func.isRequired,
    fileName: PropTypes.string.isRequired,
    missingDataFiles: PropTypes.object,
    menuItems: PropTypes.arrayOf(SEARCHABLE_MENU_ITEM),
    dispatch: PropTypes.func.isRequired,
    displayCompositeLink: PropTypes.bool.isRequired,
    analyzeCompositeAvailable: PropTypes.bool,
    currency: PropTypes.string,
    unsupportedCurrencyWarning: PropTypes.string,
    isIntradayAnalyticsEnabled: PropTypes.bool,
    isEndDateToday: PropTypes.bool,
  }

  customizeOptions = (options) => [{ value: SAME_DAY, label: "Same Day" }, ...options]

  handleResetEndDate = () => {
    const { period } = this.props
    if (period?.startDate) {
      this.props.setPeriod({ startDate: period.startDate, endDate: getToday() })
    }
  }

  setPeriod = change => {
    if (change.startDate) {
      if (change.adjusted.startDate || change.adjusted.endDate) {
        this.props.addNotification("info", null, `The selected dates have been adjusted to the nearest available date range: ${change.adjusted.startDate || change.startDate} to ${change.adjusted.endDate || change.endDate}.`)
      }

      this.props.setPeriod({
        startDate: change.adjusted.startDate || change.startDate,
        endDate: change.adjusted.endDate || change.endDate,
      })
    } else {
      this.props.setPeriod(change)
    }
  }

  getPage = ({ id, type }) => {
    const { modelId, base, security, earliestDate, trackCSVDownload, fileName, missingDataFiles, currency, setDisplayType } = this.props
    switch (type || id) {
      case "performance":
        return <SecurityProfilePerformance security={security} base={base} modelId={modelId} earliestDate={earliestDate} fileName={fileName} trackCSVDownload={trackCSVDownload} />
      case "risk":
        return <SecurityProfileRisk security={security} base={base} modelId={modelId} fileName={fileName} trackCSVDownload={trackCSVDownload} />
      case "exposure":
        return <SecurityProfileExposure security={security} base={base} modelId={modelId} fileName={fileName} trackCSVDownload={trackCSVDownload} />
      case "composition":
        return <SecurityProfileComposition security={security} base={base} modelId={modelId} fileName={fileName} trackCSVDownload={trackCSVDownload} missingDataFiles={missingDataFiles} currency={currency} />
      case "forecast":
        return <SecurityProfileForecast security={security} base={base} modelId={modelId} forecastId={id} fileName={fileName} trackCSVDownload={trackCSVDownload} />
      case "factorSensitivity":
        return <SecurityProfileFactorSensitivity {...{
          factorSensitivityType: toUpper(id),
          security,
          base,
          modelId,
          fileName,
          trackCSVDownload,
          selectPredictedFactorSensitivity: () => setDisplayType({ id: "predicted", type: "factorSensitivity" }),
        }}
        />
      case "contentset":
      default:
        return <SecurityProfileContentSet security={security} base={base} modelId={modelId} contentSet={id} fileName={fileName} trackCSVDownload={trackCSVDownload} />
    }
  }

  render() {
    const {
      watchlistsMenuVisible, toggleWatchlistsMenu, securityWatchlists, baseName, menuItems,
      onClose, period, date, startDate, endDate, earliestDate, modelId, displayType, removeBase,
      latestDate, resetDate, security, account, datesAdjusted, models, setModel, rawSecurityId,
      setDisplayType, dispatch, displayCompositeLink, analyzeCompositeAvailable, unsupportedCurrencyWarning,
      isIntradayAnalyticsEnabled, isEndDateToday,
    } = this.props
    const selectedModel = find(models, model => model.id === modelId)
    const selectedOption = getBackwardsCompatibleSelectedType(displayType)

    if (!latestDate) {
      return <NoSecurity
        onClose={onClose}
        setModel={setModel}
        security={!isEmpty(security) ? security : rawSecurityId}
        selectedModel={selectedModel}
      />
    }

    const descriptors = []
    security.gics && descriptors.push(security.gics)
    security.country && descriptors.push(security.country)
    security.currency && descriptors.push(security.currency)

    const subHeader = startDate && <div className="subheader">
      <div>
        <SearchableMenu id="security-profile-menu" items={menuItems} current={get(selectedOption, "id")} onSelect={(item) => { setDisplayType(item) }} />
        <PeriodSelector {...{ earliestDate, latestDate, endDate, startDate, period, onChange: this.setPeriod, periodAdjusted: datesAdjusted, isIntradayEnabled: isIntradayAnalyticsEnabled, isEndDateToday, handleResetOption: isIntradayAnalyticsEnabled ? this.handleResetEndDate : undefined, customizeOptions: isIntradayAnalyticsEnabled ? this.customizeOptions : undefined }} />
        {date < endDate && <SelectedDate date={date} resetHandler={resetDate} performance={displayType === "performance"} />}
      </div>
      <div>
        {setModel && <div className="model-select-wrapper">
          <ModelSelector
            alternateLayout
            disablePortal
            disableUnderline
            noSpinner
            modelId={modelId}
            modelName={selectedModel.name}
            setModel={setModel}
            resourceType={security.assetClass === "swap" ? "swap" : "security"}
            resourceId={security}
            warningMessage={unsupportedCurrencyWarning}
          />
        </div>}
        <div className="descriptors">
          {descriptors.length > 0 && descriptors.join(" - ")}
        </div>
        {security.otherClassifications.length > 0 && <Tooltip
          placement="left-start"
          classes={{
            tooltip: "descriptors-info-tooltip",
            popper: "descriptors-info-popper",
          }}
          title={
            <List>
              {map(security.otherClassifications, (item, index) => (
                <ListItem key={`list-${index}`}>
                  <ListItemText
                    primary={item.name}
                    secondary={item.value}
                  />
                </ListItem>
              ))}
            </List>
          }
        >
          <div className="additional-descriptors">
            <InfoOutlined className="additional-descriptors-icon" />
          </div>
        </Tooltip>}
      </div>
    </div>

    const header = <>
      <div>
        <div data-test-id="security-label">{security.name ? `${security.ticker} - ${security.name}` : security.ticker}</div>
        {baseName
          && <div className="compared-to">
            Compared To:&nbsp;
            {baseName}
            <Cancel onClick={removeBase} />
          </div>}
      </div>
      <UnifiedSearch account={account} modelId={modelId} hiddenTypes={["researchTopics", "currencies"]} />
      <div className="controls">
        <IconButton onClick={onClose} color="default" size="large">
          <Close />
        </IconButton>
        <WatchlistsMenu
          right
          noSpinner
          heapCreateWatchlistId="profile-create-watchlist"
          heapAddToWatchlistId="profile-apply-to-watchlist"
          security={security}
          asOf={latestDate}
          securityWatchlists={securityWatchlists}
          menuVisible={watchlistsMenuVisible}
          toggleMenu={toggleWatchlistsMenu}
          modelId={modelId}
        />
        {displayCompositeLink && <CompositeSecurityLink {...{
          dispatch,
          account,
          security,
          modelId,
          analyzeCompositeAvailable,
        }}
        />}
        <SecurityProfileToResearchTopic {...{
          dispatch,
          account,
          security,
          modelId,
          analyzeCompositeAvailable,
          setModel,
          models,
          selectedDate: date,
        }}
        />
      </div>
    </>

    const showSecurityPrice = isIntradayAnalyticsEnabled && security?.assetClass === "equity" && security?.modelProviderId

    return <Card className={`modules_security-profile ${showSecurityPrice ? "with-security-price" : ""}`}>
      <CardHeader className="card-header" disableTypography title={header} />
      <CardHeader className="card-subheader" disableTypography title={subHeader} />
      {showSecurityPrice ? (
        <div className="scrollable-content">
          <div className="security-price-wrapper"><SecurityPrice key={`${modelId}-${security.modelProviderId}`} modelId={modelId} modelProviderId={security.modelProviderId} asOf={latestDate} /></div>
          {this.getPage(selectedOption)}
        </div>
      ) : (
        this.getPage(selectedOption)
      )}
    </Card>
  }
}

// SecurityProfile is used to display more than just baskets so we need to check
// the type of the resource to avoid useless calls
export default saveRecentResources(SecurityProfile, "basketUUID", { condition: (props) => get(props, "security.assetClass") === "swap" })
